import React from "react";
import { useEffect, useRef, useState } from "react";
import { getSelectedTemplate } from "../redux/selectors";
import { apiURL, templates_sample } from "../config";
import { useSelector, useDispatch } from "react-redux";
import {
  updateSystemById,
  updateMessageById,
  updateTitleById,
  setSelectedTemplate,
  addNewTemplate,
  deleteTemplateById,
  setTemplates,
} from "../redux/templatesSlice";
import { setPrompt } from "../redux/promptSlice";
import mongoObjectId from "../utils/mongoObjectId";

const TemplateGroup = () => {
  const { systemText, templateMessage, title } =
    useSelector(getSelectedTemplate);
  const dispatch = useDispatch();
  const selectedTemplate = useSelector(
    (state) => state.templates.selectedTemplate
  );
  const user = useSelector((state) => state.user);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    dispatch(setPrompt(templateMessage));
  }, [templateMessage, dispatch]);

  // req templates from api server when user._id changes
  useEffect(() => {
    //console.log('template effect')
    let ignore = false;
    const fetchTemplates = async () => {
      try {
        const res = await fetch(`${apiURL}/api/templates`, {
          credentials: "include",
        });
        const data = await res.json();
        if (!ignore) {
          console.log(data);
          dispatch(setTemplates([...data.templates]));
          dispatch(setSelectedTemplate(null));
        } else {
          console.log("template effect ignore");
        }
      } catch (error) {
        console.log("Error fetching templates: ", error);
      }
    };
    if (user._id) {
      fetchTemplates();
    } else {
      console.log("no user. use sample templates");
      dispatch(setTemplates(templates_sample));
      dispatch(setSelectedTemplate(null));
    }
    return () => {
      ignore = true;
    };
  }, [user._id, dispatch]);

  const handleSave = async () => {
    // post the changes to api server
    async function updateTemplate() {
      try {
        const response = await fetch(
          `${apiURL}/api/templates/${selectedTemplate}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: title,
              system: systemText,
              message: templateMessage,
            }),
            credentials: "include",
          }
        );
        if (response.ok) {
          setChanged(false);
        } else {
          console.log("Error updating template");
        }
      } catch (error) {
        console.log("Error updating template: ", error);
      }
    }
    if (user._id) {
      updateTemplate();
    }
  };

  const handleDelete = async () => {
    async function deleteTemplate() {
      try {
        const response = await fetch(
          `${apiURL}/api/templates/delete/${selectedTemplate}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );
        if (response.ok) {
          console.log("Template deleted");
        } else {
          console.log("Error deleting template");
        }
      } catch (error) {
        console.log("Error deleting template: ", error);
      }
    }
    if (user._id) {
      deleteTemplate();
    }
    dispatch(deleteTemplateById(selectedTemplate));
    dispatch(setSelectedTemplate(null));
    setChanged(false);
  };

  return (
    <>
      <div className="mb-2 flex flex-row items-center">
        <h2 className="text-sm text-gray-300 px-1">Select template: </h2>
        <TemplateSelect title={title} setChanged={setChanged} />
      </div>
      {selectedTemplate && (
        <>
          <Editor
            field="Template Name"
            value={title}
            updateReducer={updateTitleById}
            setChanged={setChanged}
          />
          <Editor
            field="System Role"
            value={systemText}
            updateReducer={updateSystemById}
            setChanged={setChanged}
          />

          <Editor
            field="Prompt Template"
            value={templateMessage}
            updateReducer={updateMessageById}
            setChanged={setChanged}
          />
        </>
      )}
      <div className="flex flex-row justify-start items-center">
        {changed && (
          <div
            className={`w-fit  rounded-lg p-2 text-sm select-none  ${
              user._id
                ? "bg-green-500 cursor-pointer hover:bg-green-600 hover:scale-105 transition"
                : "bg-[hsla(var(--bg-000))] brightness-150 cursor-not-allowed"
            }`}
            onClick={handleSave}
          >
            {user._id ? "Save Changes" : "Login to save changes"}
          </div>
        )}
        {selectedTemplate && (
          <div
            className="w-8 h-8 ml-auto rounded-lg p-2 text-sm select-none border border-[hsla(var(--border-100))] bg-[hsla(var(--bg-300))] cursor-pointer hover:bg-red-600 hover:scale-105 transition"
            onClick={handleDelete}
          >
            {deleteSVG}
          </div>
        )}
      </div>
    </>
  );
};

const Editor = ({ field, value, updateReducer, setChanged }) => {
  const editorRef = useRef();
  const dispatch = useDispatch();
  const selectedTemplate = useSelector(
    (state) => state.templates.selectedTemplate
  );
  useEffect(() => {
    const resizeText = () => {
      const textarea = editorRef.current;
      textarea.style.height = "auto";
      const textHeight = textarea.scrollHeight;
      textarea.style.height = textHeight + "px";
    };
    resizeText();
  }); // run after every render
  return (
    <div className="mb-2">
      <h3 className="px-1 mb-2 text-sm text-gray-300">{field}</h3>
      <div className="relative w-full pl-9 min-h-[2rem] flex items-center bg-[hsla(var(--bg-000))] text-white rounded-xl px-4  py-2  border-[0.5px] border-[hsla(var(--border-100)/0.8)]">
        <textarea
          ref={editorRef}
          rows="1"
          className="rounded-none  focus:ring-0 focus:outline-none bg-transparent  text-white  flex-1 min-w-0 w-full text-sm resize-none h-auto max-h-40"
          placeholder="Empty"
          value={value}
          onChange={(e) => {
            setChanged(true);
            dispatch(
              updateReducer({
                id: selectedTemplate,
                text: e.target.value,
              })
            );
          }}
        ></textarea>
        <div className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6">
          {editSVG}
        </div>
      </div>
    </div>
  );
};

const TemplateSelect = ({ title, setChanged }) => {
  const dropDownButtonRef = useRef();
  const [show, setShow] = useState(false);
  const [dropDownPosition, setDropDownPosition] = useState({
    position: "top-full right-0",
    maxHeight: "200px",
  });

  const handleClick = () => {
    if (dropDownButtonRef.current) {
      const rect = dropDownButtonRef.current.getBoundingClientRect();
      const spaceBelow = Math.floor(window.innerHeight - rect.bottom - 10);
      if (spaceBelow < 150) {
        const spaceAbove = Math.floor(rect.top - 10);
        setDropDownPosition({
          position: "bottom-full right-0",
          maxHeight: `${spaceAbove}px`,
        });
      } else {
        setDropDownPosition({
          position: "top-full right-0",
          maxHeight: `${spaceBelow}px`,
        });
      }
    }
    setShow(!show);
  };

  // handle click outside the dropdown
  const handleClickOutside = (event) => {
    if (
      dropDownButtonRef.current &&
      !dropDownButtonRef.current.contains(event.target)
    ) {
      setShow(false);
    }
  };

  // add event listener when component mounts
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative select-none h-9 flex-1"
      onClick={handleClick}
      ref={dropDownButtonRef}
    >
      <div className="flex relative items-center text-white text-sm px-2 pr-8  h-full bg-[hsla(var(--bg-300))] rounded-md hover:bg-gray-700 hover:text-white cursor-pointer transition border-[1px] border-[hsla(var(--border-100))]">
        <div className={"line-clamp-1 "}>
          {" "}
          {title.length > 0 ? title : "--"}{" "}
        </div>
        <ArrowDown />
      </div>
      <TemplateDropDown
        show={show}
        setShow={setShow}
        dropDownPosition={dropDownPosition}
        setChanged={setChanged}
      />
    </div>
  );
};

const TemplateDropDown = ({ show, setShow, dropDownPosition, setChanged }) => {
  const templates = useSelector((state) => state.templates.data);
  const dispatch = useDispatch();

  return (
    <div
      className={`${!show && "hidden"} absolute ${
        dropDownPosition.position
      } w-full overflow-y-scroll z-10 py-3 pb-1  bg-[hsla(var(--bg-100))] shadow-md rounded-lg border`}
      style={{ maxHeight: dropDownPosition.maxHeight }}
    >
      <div
        className="px-4 py-1 hover:bg-[hsla(var(--bg-300))] cursor-pointer text-sm"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(setSelectedTemplate(null));
          setShow(false);
          setChanged(false);
        }}
      >
        --
      </div>
      {templates.map((template) => (
        <div
          key={template.id}
          className="px-4 py-1 hover:bg-[hsla(var(--bg-300))] cursor-pointer text-sm"
          onClick={(e) => {
            e.stopPropagation();
            dispatch(setSelectedTemplate(template.id));
            setShow(false);
            setChanged(false);
          }}
        >
          {template.title}
        </div>
      ))}
      <div
        className="px-4 py-2 hover:bg-[hsla(var(--bg-300))] cursor-pointer text-sm text-center hover:[text-shadow:_#FC0_0px_0px_5px,_#FC0_0px_0px_8px,_#FC0_0px_0px_10px]"
        onClick={(e) => {
          e.stopPropagation();
          const newid = mongoObjectId();
          dispatch(addNewTemplate(newid));
          dispatch(setSelectedTemplate(newid));
          setShow(false);
          setChanged(false);
        }}
      >
        ㊉
      </div>
    </div>
  );
};

const editSVG = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g
      id="SVGRepo_tracerCarrier"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></g>
    <g id="SVGRepo_iconCarrier">
      {" "}
      <path
        d="M18.3785 8.44975L11.4637 15.3647C11.1845 15.6439 10.8289 15.8342 10.4417 15.9117L7.49994 16.5L8.08829 13.5582C8.16572 13.1711 8.35603 12.8155 8.63522 12.5363L15.5501 5.62132M18.3785 8.44975L19.7927 7.03553C20.1832 6.64501 20.1832 6.01184 19.7927 5.62132L18.3785 4.20711C17.988 3.81658 17.3548 3.81658 16.9643 4.20711L15.5501 5.62132M18.3785 8.44975L15.5501 5.62132"
        stroke="#ffffff"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>{" "}
      <path
        d="M5 20H19"
        stroke="#ffffff"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>{" "}
    </g>
  </svg>
);

const ArrowDown = function () {
  return (
    <div className="absolute right-4 top-[57%] -translate-y-1/2">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="SVGRepo_iconCarrier">
          {" "}
          <path
            d="M19 9L14 14.1599C13.7429 14.4323 13.4329 14.6493 13.089 14.7976C12.7451 14.9459 12.3745 15.0225 12 15.0225C11.6255 15.0225 11.2549 14.9459 10.9109 14.7976C10.567 14.6493 10.2571 14.4323 10 14.1599L5 9"
            stroke="hsla(var(--text-100)/0.8)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>{" "}
        </g>
      </svg>
    </div>
  );
};

const deleteSVG = (
  <svg
    fill="#ffffff"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    stroke="#ffffff"
  >
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g
      id="SVGRepo_tracerCarrier"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></g>
    <g id="SVGRepo_iconCarrier">
      <path d="M5.755,20.283,4,8H20L18.245,20.283A2,2,0,0,1,16.265,22H7.735A2,2,0,0,1,5.755,20.283ZM21,4H16V3a1,1,0,0,0-1-1H9A1,1,0,0,0,8,3V4H3A1,1,0,0,0,3,6H21a1,1,0,0,0,0-2Z"></path>
    </g>
  </svg>
);

export default TemplateGroup;
